import {
  AlmanacConfig,
  AlmanacTypeCategory,
  CategoryClassification,
  categoryClassificationFromJSON,
  DiscriminatorTypeCategory,
  Formula,
  ModelinatorConfig,
  ModelinatorTypeCategory,
  TypeCategory,
} from "protos/almanac/almanac";
import { CropMapById } from "portal/utils/crops";
import { DisplayCrop, getLocaleCropName } from "./crops";
import { findWhere } from "./arrays";
import { keys } from "./objects";
import { PortalLanguage } from "portal/i18nConstants";
import type { TFunction } from "i18next";

export enum SIZE_INDEX {
  SMALL = 0,
  MEDIUM = 1,
  LARGE = 2,
}
export const ALL_SIZES = Object.freeze([
  SIZE_INDEX.SMALL,
  SIZE_INDEX.MEDIUM,
  SIZE_INDEX.LARGE,
] as const);

export const ALMANAC_CATEGORY_DEFAULT = "DEFAULT";

export const WEED_CATEGORY_BROADLEAF = "9fd4e9d0-2d01-4ae1-89c5-f4b4f6de9a3c";
export const WEED_CATEGORY_GRASS = "f032b206-92cf-400a-b79e-e22bdb7930e3";
export const WEED_CATEGORY_OFFSHOOT = "2e678c61-f476-4485-aa9c-6a0e4bc7c626";
export const WEED_CATEGORY_PURSLANE = "1659226a-fe1c-450c-828f-88dc8819c25d";
export const WEED_CATEGORY_BLOSSOM = "bb55614f-44df-4ee0-a754-b891a70961d2";
export const WEED_CATEGORY_PREBLOSSOM = "a0e9ec55-4414-4dd1-bf71-c19a138fedd6";
export const WEED_CATEGORY_FRUIT = "12051fbb-ed5e-4a56-b7a4-a17c7a976550";
export const WEED_CATEGORY_RUNNER = "8fcb6d5a-6c28-4b38-b48b-485178979086";
export const WEED_CATEGORY_DISREGARD = "1f8115a8-5bba-4b20-ba69-53ffb1eeb11d";

const WEED_CATEGORY_IDS = [
  WEED_CATEGORY_BROADLEAF,
  WEED_CATEGORY_GRASS,
  WEED_CATEGORY_OFFSHOOT,
  WEED_CATEGORY_PURSLANE,
  WEED_CATEGORY_BLOSSOM,
  WEED_CATEGORY_PREBLOSSOM,
  WEED_CATEGORY_FRUIT,
  WEED_CATEGORY_RUNNER,
  WEED_CATEGORY_DISREGARD,
] as const;

export type WeedCategoryId = (typeof WEED_CATEGORY_IDS)[number];

const WEED_CATEGORY_LABEL_KEYS = {
  [WEED_CATEGORY_BROADLEAF]: "models.weeds.categories.broadleaf",
  [WEED_CATEGORY_GRASS]: "models.weeds.categories.grass",
  [WEED_CATEGORY_OFFSHOOT]: "models.weeds.categories.offshoot",
  [WEED_CATEGORY_PURSLANE]: "models.weeds.categories.purslane",
  [WEED_CATEGORY_BLOSSOM]: "models.weeds.categories.blossom",
  [WEED_CATEGORY_PREBLOSSOM]: "models.weeds.categories.preblossom",
  [WEED_CATEGORY_FRUIT]: "models.weeds.categories.fruit",
  [WEED_CATEGORY_RUNNER]: "models.weeds.categories.runner",
  [WEED_CATEGORY_DISREGARD]: "models.weeds.categories.disregard",
} satisfies Record<WeedCategoryId, string>;

const WEED_CATEGORIES = new Set<string>(WEED_CATEGORY_IDS);

export const SIZE_SMALL_TO_MEDIUM = 8;
export const SIZE_MEDIUM_TO_LARGE = 25;
export const DEFAULT_SIZES = [SIZE_SMALL_TO_MEDIUM, SIZE_MEDIUM_TO_LARGE];

type FormulaFieldConstraints = {
  min?: number;
  max?: number;
  step?: number;
  default: number;
};
export const FORMULA_FIELD_CONSTRAINTS: Record<
  keyof Formula,
  FormulaFieldConstraints
> = {
  multiplier: { min: 0, step: 0.1, default: 25 },
  offset: { min: 0, step: 100, default: 0 },
  exponent: { min: 1, step: 0.1, default: 1.23 },
  fineTuneMultiplier: { min: 1, max: 10, step: 1, default: 5 },
  maxTime: { min: 0, step: 100, default: 3000 },
  fineTuneMultiplierVal: { min: 0, max: 1, step: 0.1, default: 0.25 },
} as const;

const DEFAULT_FORMULA = Formula.fromPartial({
  multiplier: FORMULA_FIELD_CONSTRAINTS.multiplier.default,
  exponent: FORMULA_FIELD_CONSTRAINTS.exponent.default,
  fineTuneMultiplier: FORMULA_FIELD_CONSTRAINTS.fineTuneMultiplier.default,
  maxTime: FORMULA_FIELD_CONSTRAINTS.maxTime.default,
  fineTuneMultiplierVal:
    FORMULA_FIELD_CONSTRAINTS.fineTuneMultiplierVal.default,
  offset: FORMULA_FIELD_CONSTRAINTS.offset.default,
});

export const DEFAULT_CATEGORY = AlmanacTypeCategory.fromPartial({
  type: {
    category: "",
    classification: categoryClassificationFromJSON(
      CategoryClassification.CATEGORY_WEED
    ),
  },
  sizes: DEFAULT_SIZES,
  formulas: [DEFAULT_FORMULA, DEFAULT_FORMULA, DEFAULT_FORMULA],
});

export const isCategorySynced = (category: AlmanacTypeCategory): boolean => {
  for (const key of keys(DEFAULT_FORMULA)) {
    const values = new Set();
    for (const formula of category.formulas) {
      values.add(formula[key]);
    }
    if (values.size > 1) {
      return false;
    }
  }
  return true;
};

export const getFormulaBySize = (
  category: AlmanacTypeCategory,
  size: SIZE_INDEX
): Formula => {
  const formula = category.formulas[size];
  if (!formula) {
    console.error(`Formula for size ${size} not found`);
    return Formula.fromPartial({});
  }
  return formula;
};

export const DEFAULT_WEED_BROADLEAF = AlmanacTypeCategory.fromPartial({
  type: {
    category: WEED_CATEGORY_BROADLEAF,
    classification: categoryClassificationFromJSON(
      CategoryClassification.CATEGORY_WEED
    ),
  },
  sizes: [10, 20],
  formulas: [
    {
      multiplier: 12,
      exponent: 1.45,
      fineTuneMultiplier: 5,
      maxTime: 3000,
      fineTuneMultiplierVal: 0.2,
      offset: 0,
    },
    {
      multiplier: 12,
      exponent: 1.45,
      fineTuneMultiplier: 5,
      maxTime: 3000,
      fineTuneMultiplierVal: 0.2,
      offset: 0,
    },
    {
      multiplier: 12,
      exponent: 1.45,
      fineTuneMultiplier: 5,
      maxTime: 3000,
      fineTuneMultiplierVal: 0.2,
      offset: 0,
    },
  ],
});

export const DEFAULT_WEED_GRASS = AlmanacTypeCategory.fromPartial({
  type: {
    category: WEED_CATEGORY_GRASS,
    classification: categoryClassificationFromJSON(
      CategoryClassification.CATEGORY_WEED
    ),
  },
  sizes: [8, 20],
  formulas: [
    {
      multiplier: 12,
      exponent: 1.23,
      fineTuneMultiplier: 5,
      maxTime: 3000,
      fineTuneMultiplierVal: 0.2,
      offset: 0,
    },
    {
      multiplier: 12,
      exponent: 1.23,
      fineTuneMultiplier: 5,
      maxTime: 3000,
      fineTuneMultiplierVal: 0.2,
      offset: 0,
    },
    {
      multiplier: 12,
      exponent: 1.23,
      fineTuneMultiplier: 5,
      maxTime: 3000,
      fineTuneMultiplierVal: 0.2,
      offset: 0,
    },
  ],
});

export const DEFAULT_WEED_OFFSHOOT = AlmanacTypeCategory.fromPartial({
  type: {
    category: WEED_CATEGORY_OFFSHOOT,
    classification: categoryClassificationFromJSON(
      CategoryClassification.CATEGORY_WEED
    ),
  },
  sizes: DEFAULT_SIZES,
  formulas: [
    {
      multiplier: 12,
      exponent: 1.37,
      fineTuneMultiplier: 4,
      maxTime: 3000,
      fineTuneMultiplierVal: 0.2,
      offset: 0,
    },
    {
      multiplier: 12,
      exponent: 1.37,
      fineTuneMultiplier: 5,
      maxTime: 3000,
      fineTuneMultiplierVal: 0.2,
      offset: 0,
    },
    {
      multiplier: 12,
      exponent: 1.37,
      fineTuneMultiplier: 5,
      maxTime: 3000,
      fineTuneMultiplierVal: 0.2,
      offset: 0,
    },
  ],
});

export const DEFAULT_WEED_PURSLANE = AlmanacTypeCategory.fromPartial({
  type: {
    category: WEED_CATEGORY_PURSLANE,
    classification: categoryClassificationFromJSON(
      CategoryClassification.CATEGORY_WEED
    ),
  },
  sizes: [8, 20],
  formulas: [
    {
      multiplier: 14,
      exponent: 1.4,
      fineTuneMultiplier: 5,
      maxTime: 3000,
      fineTuneMultiplierVal: 0.2,
      offset: 0,
    },
    {
      multiplier: 14,
      exponent: 1.4,
      fineTuneMultiplier: 5,
      maxTime: 3000,
      fineTuneMultiplierVal: 0.2,
      offset: 0,
    },
    {
      multiplier: 14,
      exponent: 1.4,
      fineTuneMultiplier: 5,
      maxTime: 3000,
      fineTuneMultiplierVal: 0.2,
      offset: 0,
    },
  ],
});

export const getFallbackAlmanac = (t: TFunction): AlmanacConfig =>
  AlmanacConfig.fromPartial({
    name: t("utils.descriptors.default"),
    protected: false,
    // category values copied from prod on Oct 8 2025
    categories: [
      DEFAULT_WEED_BROADLEAF,
      DEFAULT_WEED_GRASS,
      DEFAULT_WEED_OFFSHOOT,
      DEFAULT_WEED_PURSLANE,
      {
        ...DEFAULT_CATEGORY,
        type: {
          category: ALMANAC_CATEGORY_DEFAULT,
          classification: categoryClassificationFromJSON(
            CategoryClassification.CATEGORY_WEED
          ),
        },
      },
      {
        type: {
          category: "6b5f9c72-7c59-4af6-ab96-c46db49649a2",
          classification: CategoryClassification.CATEGORY_CROP,
        },
        sizes: [4, 25],
        formulas: [
          {
            multiplier: 40,
            exponent: 1.23,
            fineTuneMultiplier: 5,
            maxTime: 3000,
            fineTuneMultiplierVal: 0.25,
            offset: 0,
          },
          {
            multiplier: 40,
            exponent: 1.23,
            fineTuneMultiplier: 5,
            maxTime: 3000,
            fineTuneMultiplierVal: 0.25,
            offset: 0,
          },
          {
            multiplier: 40,
            exponent: 1.23,
            fineTuneMultiplier: 5,
            maxTime: 3000,
            fineTuneMultiplierVal: 0.25,
            offset: 0,
          },
        ],
      },
      {
        type: {
          category: "cf3a51e4-fe68-447f-b431-7c093c816f9a",
          classification: CategoryClassification.CATEGORY_CROP,
        },
        sizes: [4, 25],
        formulas: [
          {
            multiplier: 40,
            exponent: 1.23,
            fineTuneMultiplier: 5,
            maxTime: 3000,
            fineTuneMultiplierVal: 0.25,
            offset: 0,
          },
          {
            multiplier: 40,
            exponent: 1.23,
            fineTuneMultiplier: 5,
            maxTime: 3000,
            fineTuneMultiplierVal: 0.25,
            offset: 0,
          },
          {
            multiplier: 40,
            exponent: 1.23,
            fineTuneMultiplier: 5,
            maxTime: 3000,
            fineTuneMultiplierVal: 0.25,
            offset: 0,
          },
        ],
      },
      {
        type: {
          category: "2a377913-7e48-44c5-9f07-9da9cd43e804",
          classification: CategoryClassification.CATEGORY_CROP,
        },
        sizes: [4, 25],
        formulas: [
          {
            multiplier: 40,
            exponent: 1.25,
            fineTuneMultiplier: 5,
            maxTime: 3000,
            fineTuneMultiplierVal: 0.25,
            offset: 0,
          },
          {
            multiplier: 40,
            exponent: 1.25,
            fineTuneMultiplier: 5,
            maxTime: 3000,
            fineTuneMultiplierVal: 0.25,
            offset: 0,
          },
          {
            multiplier: 40,
            exponent: 1.25,
            fineTuneMultiplier: 5,
            maxTime: 3000,
            fineTuneMultiplierVal: 0.25,
            offset: 0,
          },
        ],
      },
      {
        type: {
          category: "0c065c8e-2996-4bb1-8500-87bb5850c91e",
          classification: CategoryClassification.CATEGORY_CROP,
        },
        sizes: [4, 25],
        formulas: [
          {
            multiplier: 40,
            exponent: 1.23,
            fineTuneMultiplier: 5,
            maxTime: 3000,
            fineTuneMultiplierVal: 0.2,
            offset: 0,
          },
          {
            multiplier: 40,
            exponent: 1.23,
            fineTuneMultiplier: 5,
            maxTime: 3000,
            fineTuneMultiplierVal: 0.2,
            offset: 0,
          },
          {
            multiplier: 40,
            exponent: 1.23,
            fineTuneMultiplier: 5,
            maxTime: 3000,
            fineTuneMultiplierVal: 0.2,
            offset: 0,
          },
        ],
      },
    ],
  });

export const getWeedCategoryName = (
  t: TFunction,
  uuid: string
): string | undefined => {
  const key = (WEED_CATEGORY_LABEL_KEYS as Record<string, string | undefined>)[
    uuid
  ];
  return key === undefined ? undefined : t(key);
};

export const filterCategories = <
  T extends
    | DiscriminatorTypeCategory
    | AlmanacTypeCategory
    | ModelinatorTypeCategory
>(
  categories?: T[]
): {
  weedCategories: T[];
  cropCategories: T[];
  defaultCategory: T | undefined;
} => {
  const defaultCategory = categories?.find(
    (category) => category.type?.category === ALMANAC_CATEGORY_DEFAULT
  );
  const weedCategories: T[] =
    categories?.filter(
      (category) =>
        (category.type?.classification ===
          CategoryClassification.CATEGORY_WEED ||
          WEED_CATEGORIES.has(category.type?.category ?? "")) &&
        category !== defaultCategory
    ) ?? [];
  const cropCategories: T[] =
    categories?.filter(
      (category) =>
        category.type?.classification !==
          CategoryClassification.CATEGORY_WEED &&
        !WEED_CATEGORIES.has(category.type?.category ?? "") &&
        category !== defaultCategory
    ) ?? [];

  return { weedCategories, cropCategories, defaultCategory };
};

export interface FineTuneMultipliers {
  small: number;
  medium: number;
  large: number;
}

export const getFineTuneMultipliers = (
  category: AlmanacTypeCategory
): FineTuneMultipliers => ({
  small: getFormulaBySize(category, 0).fineTuneMultiplier,
  medium: getFormulaBySize(category, 1).fineTuneMultiplier,
  large: getFormulaBySize(category, 2).fineTuneMultiplier,
});

export const withFineTuneMulipliers = (
  category: AlmanacTypeCategory,
  fineTuneMultipliers: FineTuneMultipliers
): AlmanacTypeCategory => {
  const updated = structuredClone(category);
  getFormulaBySize(updated, 0).fineTuneMultiplier = fineTuneMultipliers.small;
  getFormulaBySize(updated, 1).fineTuneMultiplier = fineTuneMultipliers.medium;
  getFormulaBySize(updated, 2).fineTuneMultiplier = fineTuneMultipliers.large;
  return updated;
};

export function getCategoryTitle(
  t: TFunction,
  type: TypeCategory | undefined,
  cropData:
    | { crops: DisplayCrop[] }
    | { crops: CropMapById; locale: PortalLanguage }
    | undefined
): string {
  if (!type?.category) {
    return t("models.weeds.categories.unknown");
  }
  if (type.category === ALMANAC_CATEGORY_DEFAULT) {
    return t("utils.descriptors.default");
  }

  const weedCatName = getWeedCategoryName(t, type.category);
  if (weedCatName) {
    return weedCatName;
  }

  if (cropData && "locale" in cropData) {
    const crop = cropData.crops[type.category];
    return crop === undefined
      ? t("models.crops.categories.unknown")
      : getLocaleCropName(crop, cropData.locale);
  }

  const crop = findWhere(cropData?.crops, { id: type.category });
  return crop === undefined
    ? t("models.crops.categories.unknown")
    : crop.localeName;
}

export const getModelinatorId = (
  serial: string,
  modelinator: ModelinatorConfig
): string => `${serial}/${modelinator.cropId}-${modelinator.modelId}`;
